import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";

const links = [
  { to: "/events", label: "Events" },
  { to: "/races", label: "Races" },
  { to: "/challenges", label: "Challenges" },
  { to: "/activity", label: "Activity", auth: true },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/community", label: "Community" },
  { to: "/tips", label: "Tips" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="inline-block w-2 h-6 bg-orange-600 group-hover:bg-orange-500 transition" />
          <span className="font-display text-xl leading-none">
            OSCR<span className="text-orange-500">·</span>
          </span>
          <span className="hidden sm:inline font-display text-xs text-zinc-500 tracking-widest pl-2 border-l border-zinc-800 ml-1">
            ODISHA STEEL CITY RUNNERS
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 font-display text-sm tracking-wider transition ${
                  isActive ? "text-orange-500" : "text-zinc-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard">
                <Button variant="ghost" className="rounded-none font-display tracking-wider text-zinc-300 hover:text-white hover:bg-zinc-900">
                  Dashboard
                </Button>
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin">
                  <Button variant="ghost" className="rounded-none font-display tracking-wider text-orange-500 hover:bg-zinc-900">
                    Admin
                  </Button>
                </Link>
              )}
              <Button onClick={doLogout} data-testid="logout-btn" variant="outline" className="rounded-none border-zinc-700 bg-transparent font-display tracking-wider hover:bg-zinc-900">
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="rounded-none font-display tracking-wider text-zinc-300 hover:text-white hover:bg-zinc-900">
                  Log in
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider">
                  Join the pack
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-800 bg-zinc-950">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.filter((l) => !l.auth || user).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-2 py-2 font-display tracking-wider ${isActive ? "text-orange-500" : "text-zinc-300"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="px-2 py-2 font-display tracking-wider text-zinc-300">
                  Dashboard
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="px-2 py-2 font-display tracking-wider text-orange-500">
                    Admin
                  </Link>
                )}
                <button onClick={doLogout} className="text-left px-2 py-2 font-display tracking-wider text-zinc-300">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-2 py-2 font-display tracking-wider text-zinc-300">
                  Log in
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="px-2 py-2 font-display tracking-wider text-orange-500">
                  Join the pack
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
